<template>
  <div class="container py-5 pt-3 pt-xl-5">
    <ProgressNavComponent
      v-if="signupStep > 1 && !changingInformationDisabled"
      :steps="navSteps"
      :setStep="setStep"
    />

    <!-- STEP 1: Country selection and onboarding type -->
    <div v-if="shownStep.step_name === StepName.CHOOSE_ONBOARDING">
      <CountryPickerComponent />

      <ChooseOnboardingComponent
        :addAuthorizationAgreementFlow="createAuthorizationFlow"
        :addNewDeviceFlow="createTransferFlow"
        :countryLocale="'fi'"
      />
    </div>

    <!-- STEP 2: Signatory and company -->
    <ContactReviewConfigurationComponent
      v-if="shownStep.step_name === StepName.CONTACT_REVIEW"
    />

    <!-- STEP 3: Contract review -->
    <ContractReviewConfigurationComponent
      v-if="
        shownStep.step_name === StepName.CONTRACT_REVIEW && companyIndex !== -1
      "
      :accountData="accountData"
      :authorization="!!addAuthorizationAgreement"
      @createContainerForStartSigningReady="storeCreateContainerForStartSigning"
      @disableChangingInformation="handleDisableChangingInformation"
    />

    <!-- Buttons -->
    <div
      v-if="signupStep > 1 && !changingInformationDisabled"
      class="d-flex justify-content-between w-100"
    >
      <button
        class="btn btn-secondary me-2"
        :disabled="isLoading"
        @click="setStep(signupStep - 1)"
      >
        {{ $t("signup.navsteps.back") }}
      </button>

      <button
        class="btn btn-primary"
        :disabled="isLoading"
        @click="
          shownStep.step_name === StepName.CONTRACT_REVIEW
            ? createContainerForStartSigningFunction()
            : setStep(signupStep + 1)
        "
      >
        {{
          shownStep.step_name === StepName.CONTRACT_REVIEW
            ? $t("signup.navsteps.to_signature_page")
            : $t("signup.navsteps.next")
        }}
      </button>
    </div>
    <LoadingOverlayComponent v-if="isLoading" />
    <AccountDataDebugComponent
      :accountData="accountData"
      class="mb-4"
    ></AccountDataDebugComponent>
  </div>
</template>

<script setup lang="ts">
import AccountDataDebugComponent from "@/components/common/AccountDataDebugComponent.vue";
import LoadingOverlayComponent from "@/components/common/LoadingOverlayComponent.vue";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import { useQueryManager } from "@/composables/useQueryManager";
import { useStepFunctions, StepConfig } from "@/composables/useStepFunctions";
import ChooseOnboardingComponent from "@/signup/ChooseOnboardingComponent.vue";
import { useSignupCompanySelection } from "@/signup/composables/useSignupCompanySelection";
import { useSignupStepSave } from "@/signup/composables/useSignupStepSave";
import CountryPickerComponent from "@/signup/CountryPickerComponent.vue";
import ContactReviewConfigurationComponent from "@/signup/fi/ContactReviewConfigurationComponent.vue";
import ContractReviewConfigurationComponent from "@/signup/fi/ContractReviewConfigurationComponent.vue";
import ProgressNavComponent from "@/signup/ProgressNavComponent.vue";
import { useSignupStore } from "@/signup/signup";
import { storeToRefs } from "pinia";
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";

/**
 * Finland: the producer either authorizes Soldera on their own Fingrid registry account, or
 * transfers their devices into Soldera's. Both paths are the same three steps and differ only in
 * the documents signed, which `addAuthorizationAgreement` in the URL picks.
 */
enum StepName {
  CHOOSE_ONBOARDING,
  CONTACT_REVIEW,
  CONTRACT_REVIEW,
}

const createContainerForStartSigningFunction = ref<() => void | Promise<void>>(
  () => undefined
);

const isLoading = ref(true);
const signupStore = useSignupStore();
const { account: accountData, companyIndex } = storeToRefs(signupStore);
const { getQueryValue, parsers } = useQueryManager();
const signupStep = getQueryValue("step", (value) =>
  value ? parseInt(value, 10) : 1
);
const addAuthorizationAgreement = getQueryValue(
  "addAuthorizationAgreement",
  parsers.boolean
);

const { postStepCallback } = useSignupStepSave(isLoading);
const { t } = useI18n();
const { choosePath } = useSignupCompanySelection("FI", isLoading);
const { globalErrorHandler } = useGlobalHandlers();

const STEP_CONFIG: Record<StepName, StepConfig> = {
  [StepName.CHOOSE_ONBOARDING]: {
    step_name: StepName.CHOOSE_ONBOARDING,
    title: t("signup.navsteps.fi.flow_selection"),
    titleKey: "signup.navsteps.fi.flow_selection",
  },
  [StepName.CONTACT_REVIEW]: {
    step_name: StepName.CONTACT_REVIEW,
    title: t("signup.navsteps.fi.contact_details"),
    titleKey: "signup.navsteps.fi.contact_details",
    apiEndpoint: "/api/fi/update-company",
    postStepCallback: postStepCallback,
    preStepCallback: async (_: StepConfig) => {
      signupStore.createCompanyIfMissing();
      return true;
    },
  },
  [StepName.CONTRACT_REVIEW]: {
    step_name: StepName.CONTRACT_REVIEW,
    title: t("signup.navsteps.fi.contract_review"),
    titleKey: "signup.navsteps.fi.contract_review",
    postStepCallback: postStepCallback,
  },
};

const flowSteps = computed(() => [
  STEP_CONFIG[StepName.CHOOSE_ONBOARDING],
  STEP_CONFIG[StepName.CONTACT_REVIEW],
  STEP_CONFIG[StepName.CONTRACT_REVIEW],
]);

const { shownStep, setStep, navSteps, invokePreStepCallback } =
  useStepFunctions(flowSteps, signupStep);

const storeCreateContainerForStartSigning = (
  createContainerForStartSigning: () => Promise<void>
) => {
  createContainerForStartSigningFunction.value = createContainerForStartSigning;
};

const changingInformationDisabled = ref(false);

const handleDisableChangingInformation = () => {
  changingInformationDisabled.value = true;
};

const pickPath = async (authorization: boolean) => {
  try {
    await choosePath({ addAuthorizationAgreement: String(authorization) });
    await setStep(2);
  } catch (error) {
    globalErrorHandler(error);
  }
};

const createAuthorizationFlow = () => pickPath(true);
const createTransferFlow = () => pickPath(false);

onMounted(async () => {
  await invokePreStepCallback();
  isLoading.value = false;
});
</script>
